type ProductLike = {
  image?: string | null;
  largeImage?: string | null;
};

type SectionLike = {
  image?: string | null;
  products?: ProductLike[] | null;
};

type MenuLike = {
  menuSections?: SectionLike[] | null;
};

type PreloadMenuImagesOptions = {
  onProgress?: (progress: { loaded: number; total: number; url: string }) => void;
  timeoutMs?: number;
};

const imagePreloadCache = new Map<string, Promise<void>>();

function toUrl(value: unknown) {
  const url = String(value ?? "").trim();
  return url || "";
}

function preloadImage(url: string, timeoutMs = 12_000) {
  if (!url) return Promise.resolve();
  const cached = imagePreloadCache.get(url);
  if (cached) return cached;

  const promise = new Promise<void>((resolve) => {
    if (typeof window === "undefined" || typeof Image === "undefined") {
      resolve();
      return;
    }

    const img = new Image();
    let settled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const done = () => {
      if (settled) return;
      settled = true;
      if (timeout) clearTimeout(timeout);
      resolve();
    };
    img.onload = done;
    img.onerror = done;
    img.src = url;

    timeout = setTimeout(done, timeoutMs);

    if (img.complete) {
      done();
      return;
    }

    if (typeof img.decode === "function") {
      img.decode().then(done).catch(done);
    }
  });

  imagePreloadCache.set(url, promise);
  return promise;
}

export async function preloadMenuImages(menus: MenuLike[], options?: PreloadMenuImagesOptions) {
  const urls = new Set<string>();

  for (const menu of menus || []) {
    for (const section of menu?.menuSections || []) {
      const sectionImage = toUrl(section?.image);
      if (sectionImage) urls.add(sectionImage);

      for (const product of section?.products || []) {
        const productImage = toUrl(product?.image);
        const productLargeImage = toUrl(product?.largeImage);
        if (productImage) urls.add(productImage);
        if (productLargeImage) urls.add(productLargeImage);
      }
    }
  }

  const imageUrls = Array.from(urls);
  let loaded = 0;
  const total = imageUrls.length;
  if (total === 0) return;

  await Promise.allSettled(
    imageUrls.map(async (url) => {
      await preloadImage(url, options?.timeoutMs);
      loaded += 1;
      options?.onProgress?.({ loaded, total, url });
    })
  );
}
